import React, { createContext, useState, useEffect } from 'react';
import api from '../utils/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [patientUser, setPatientUser] = useState(null);
  const [doctorUser, setDoctorUser] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);

  // Validate and refresh tokens for a specific role
  const validateToken = async (role, accessTokenKey, refreshTokenKey, idKey, profileEndpoint, setter) => {
    const accessToken = localStorage.getItem(accessTokenKey);
    const refreshToken = localStorage.getItem(refreshTokenKey);
    const id = localStorage.getItem(idKey);
    const storedAdminRole = localStorage.getItem('adminRole');

    if (!accessToken || !id) {
      console.log(`No valid ${role} credentials found in localStorage`);
      setter(null); // Ensure state is cleared if no credentials exist
      return;
    }

    console.log(`Validating ${role} token: ${accessToken.substring(0, 10)}...`);
    try {
      const response = await api.get(profileEndpoint, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      console.log(`${role} profile loaded:`, response.data);
      const userData = {
        id,
        role: role === 'admin' ? (response.data.role || storedAdminRole || 'admin') : role,
        accessToken,
        refreshToken: refreshToken || '',
        name: response.data.name || `${role} User`,
      };
      setter(userData);
      if (role === 'admin' && response.data.role) {
        localStorage.setItem('adminRole', response.data.role);
      }
    } catch (error) {
      console.error(`${role} profile fetch failed:`, error.response?.data || error.message);
      if (error.response?.status === 401 && refreshToken) {
        console.log(`Attempting ${role} token refresh with: ${refreshToken.substring(0, 10)}...`);
        const refreshEndpoint = role === 'admin' ? '/admin/refresh' : '/auth/refresh';
        try {
          const refreshResponse = await api.post(refreshEndpoint, { refreshToken });
          const { accessToken: newAccessToken } = refreshResponse.data;
          console.log(`${role} token refreshed: ${newAccessToken.substring(0, 10)}...`);
          localStorage.setItem(accessTokenKey, newAccessToken);
          const profileResponse = await api.get(profileEndpoint, {
            headers: { Authorization: `Bearer ${newAccessToken}` },
          });
          const userData = {
            id,
            role: role === 'admin' ? (profileResponse.data.role || storedAdminRole || 'admin') : role,
            accessToken: newAccessToken,
            refreshToken,
            name: profileResponse.data.name || `${role} User`,
          };
          setter(userData);
          if (role === 'admin' && profileResponse.data.role) {
            localStorage.setItem('adminRole', profileResponse.data.role);
          }
        } catch (refreshError) {
          console.error(`${role} refresh failed:`, refreshError.response?.data || refreshError.message);
          setter(null);
          localStorage.removeItem(accessTokenKey);
          localStorage.removeItem(refreshTokenKey);
          localStorage.removeItem(idKey);
          if (role === 'admin') localStorage.removeItem('adminRole');
        }
      } else {
        setter(null);
        localStorage.removeItem(accessTokenKey);
        localStorage.removeItem(refreshTokenKey);
        localStorage.removeItem(idKey);
        if (role === 'admin') localStorage.removeItem('adminRole');
      }
    }
  };

  // Initialize auth state on mount
  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      await Promise.all([
        validateToken('patient', 'patientAccessToken', 'patientRefreshToken', 'patientId', '/patients/profile', setPatientUser),
        validateToken('doctor', 'doctorAccessToken', 'doctorRefreshToken', 'doctorId', '/doctors/profile', setDoctorUser),
        validateToken('admin', 'adminAccessToken', 'adminRefreshToken', 'adminId', '/admin/profile', setAdminUser),
      ]);
      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Login function with role-specific handling
  const login = async (userData) => {
    console.log('Login called with:', userData);
    const { id, role, accessToken, refreshToken, name } = userData;

    if (!id || !role || !accessToken) {
      console.error('Invalid login data:', userData);
      return false;
    }

    const user = {
      id,
      role,
      accessToken,
      refreshToken: refreshToken || '',
      name: name || `${role} User`,
    };

    setAuthLoading(true);
    try {
      if (role === 'patient') {
        // Only update patient state and storage
        setPatientUser(user);
        localStorage.setItem('patientAccessToken', accessToken);
        localStorage.setItem('patientRefreshToken', refreshToken || '');
        localStorage.setItem('patientId', id);
      } else if (role === 'doctor') {
        // Only update doctor state and storage
        setDoctorUser(user);
        localStorage.setItem('doctorAccessToken', accessToken);
        localStorage.setItem('doctorRefreshToken', refreshToken || '');
        localStorage.setItem('doctorId', id);
      } else if (['Super Admin', 'Operations Manager', 'Doctor Manager', 'Finance Manager', 'Support Staff'].includes(role)) {
        // Only update admin state and storage
        setAdminUser(user);
        localStorage.setItem('adminAccessToken', accessToken);
        localStorage.setItem('adminRefreshToken', refreshToken || '');
        localStorage.setItem('adminId', id || 'admin');
        localStorage.setItem('adminRole', role);
      } else {
        console.warn(`Unknown role: ${role}`);
        return false;
      }
      console.log(`Login successful for role: ${role}`);
      return true;
    } catch (error) {
      console.error(`Login failed for ${role}:`, error);
      return false;
    } finally {
      setAuthLoading(false);
    }
  };

  // Logout function with role-specific handling
  const logout = (role) => {
    console.log(`Logging out ${role}`);
    if (role === 'patient') {
      setPatientUser(null);
      localStorage.removeItem('patientAccessToken');
      localStorage.removeItem('patientRefreshToken');
      localStorage.removeItem('patientId');
    } else if (role === 'doctor') {
      setDoctorUser(null);
      localStorage.removeItem('doctorAccessToken');
      localStorage.removeItem('doctorRefreshToken');
      localStorage.removeItem('doctorId');
    } else if (role === 'admin') {
      setAdminUser(null);
      localStorage.removeItem('adminAccessToken');
      localStorage.removeItem('adminRefreshToken');
      localStorage.removeItem('adminId');
      localStorage.removeItem('adminRole');
    } else {
      console.warn(`Unknown role for logout: ${role}`);
    }
  };

  // Logout all roles (optional utility)
  const logoutAll = () => {
    console.log('Logging out all roles');
    setPatientUser(null);
    setDoctorUser(null);
    setAdminUser(null);
    localStorage.clear(); // Clear all localStorage (be cautious with this)
  };

  // Trigger auth loading state for UI feedback
  const triggerAuthLoading = () => {
    setAuthLoading(true);
    setTimeout(() => setAuthLoading(false), 500); // Reduced to 500ms for faster feedback
  };

  return (
    <AuthContext.Provider value={{ 
      patientUser, 
      doctorUser, 
      adminUser, 
      loading, 
      authLoading,
      login, 
      logout, 
      logoutAll, // Added for flexibility
      triggerAuthLoading 
    }}>
      {loading ? (
        <div className="auth-loading-overlay">
          <div className="auth-loading-spinner"></div>
          <p>Loading authentication...</p>
        </div>
      ) : authLoading ? (
        <div className="auth-loading-overlay">
          <div className="auth-loading-spinner"></div>
          <p>Processing...</p>
        </div>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

export default AuthProvider;